import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, BACKEND_URL } from "../App";
import { Heart, Video, Upload, Check, ArrowLeft, User, MessageSquare, Camera, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

export default function TreeSubmitPage() {
  const { weddingId } = useParams();
  const videoInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadStep, setUploadStep] = useState("");
  const [formData, setFormData] = useState({ guest_name: "", message: "" });
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    const fetchWedding = async () => {
      try {
        const res = await axios.get(`${API}/weddings/${weddingId}`);
        setWedding(res.data);
      } catch { toast.error("Wedding not found"); }
      finally { setLoading(false); }
    };
    fetchWedding();
  }, [weddingId]);

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error("Video must be under 50MB"); return; }
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Photo must be under 10MB"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.guest_name.trim()) { toast.error("Please enter your name"); return; }
    setSubmitting(true);

    try {
      let photoUrl = null;
      let videoUrl = null;

      // Step 1 — Upload photo
      if (photoFile) {
        setUploadStep("Uploading photo...");
        const fd = new FormData();
        fd.append("file", photoFile);
        fd.append("wedding_id", weddingId);
        fd.append("media_type", "image");
        const res = await axios.post(`${API}/media/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        photoUrl = res.data.url;
      }

      // Step 2 — Upload video
      if (videoFile) {
        setUploadStep("Uploading video...");
        const fd = new FormData();
        fd.append("file", videoFile);
        fd.append("wedding_id", weddingId);
        fd.append("media_type", "video");
        const res = await axios.post(`${API}/media/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        videoUrl = res.data.url;
      }

      // Step 3 — Create tree submission
      setUploadStep("Sending your message...");
      await axios.post(`${API}/tree`, {
        wedding_id: weddingId,
        guest_name: formData.guest_name,
        message: formData.message,
        video_url: videoUrl,
        photo_url: photoUrl,
      });

      setSubmitted(true);
      toast.success("Your message has been submitted!");
    } catch {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
      setUploadStep("");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!wedding) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center px-6">
        <div className="text-center">
          <Heart className="w-16 h-16 text-wedding-gold/30 mx-auto mb-6" />
          <h1 className="font-serif text-3xl text-wedding-ivory mb-4">Wedding Not Found</h1>
          <Link to="/" className="btn-secondary">Return Home</Link>
        </div>
      </div>
    );
  }

  const coupleName = `${wedding.couple_name_1} & ${wedding.couple_name_2}`;

  if (submitted) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center px-6" data-testid="submit-success">
        <motion.div className="text-center max-w-md" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-wedding-gold/20 flex items-center justify-center">
            <Check className="w-10 h-10 text-wedding-gold" />
          </div>
          <h1 className="font-serif text-3xl text-wedding-ivory mb-4">Thank You!</h1>
          <p className="text-wedding-ivory/60 mb-8">
            Your message for {coupleName} has been received. It will appear on their Wedding Tree once approved.
          </p>
          <Link to={`/${wedding.slug}`} className="btn-primary inline-block">View Wedding Page</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wedding-black" data-testid="tree-submit-page">
      {/* Hidden file inputs */}
      <input ref={videoInputRef} type="file" accept="video/mp4,video/quicktime,video/webm" onChange={handleVideoChange} className="hidden" data-testid="video-input" />
      <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" data-testid="photo-input" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-wedding-black/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`/${wedding.slug}`} className="flex items-center gap-2 text-wedding-ivory/60 hover:text-wedding-gold transition-colors">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm">Back to Wedding</span>
          </Link>
          <div className="font-serif text-wedding-gold">MemoraOrbs</div>
        </div>
      </header>

      {/* Main */}
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-xl mx-auto">
          <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Heart className="w-12 h-12 text-wedding-gold mx-auto mb-6" />
            <h1 className="font-serif text-3xl md:text-4xl text-wedding-ivory mb-4">Leave a Message</h1>
            <p className="text-wedding-ivory/60">Share your wishes with {coupleName}</p>
          </motion.div>

          <motion.form onSubmit={handleSubmit} className="space-y-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            {/* Photo Upload — Circular Selfie */}
            <div className="flex flex-col items-center">
              <Label className="text-wedding-ivory/80 text-xs uppercase tracking-wider flex items-center gap-2 mb-4">
                <Camera className="w-4 h-4" />
                Photo for Your Tree Bubble (Optional)
              </Label>
              <div className="relative">
                {photoPreview ? (
                  <div className="relative">
                    <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-wedding-gold/50 shadow-lg shadow-wedding-gold/10">
                      <img src={photoPreview} alt="Your photo" className="w-full h-full object-cover" data-testid="photo-preview" />
                    </div>
                    <button
                      type="button"
                      onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                      className="absolute -top-1 -right-1 w-7 h-7 bg-wedding-black border border-white/20 rounded-full flex items-center justify-center hover:bg-red-900/50 transition-colors"
                      data-testid="remove-photo-btn"
                    >
                      <X className="w-3.5 h-3.5 text-wedding-ivory" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="w-28 h-28 rounded-full border-2 border-dashed border-white/20 hover:border-wedding-gold/50 transition-colors flex flex-col items-center justify-center gap-1 group"
                    data-testid="upload-photo-btn"
                  >
                    <Camera className="w-6 h-6 text-wedding-ivory/30 group-hover:text-wedding-gold/60 transition-colors" />
                    <span className="text-wedding-ivory/30 text-[10px] group-hover:text-wedding-ivory/50 transition-colors">Add Photo</span>
                  </button>
                )}
              </div>
              <p className="text-wedding-ivory/30 text-xs mt-2">Your face will appear on the tree</p>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-wedding-ivory/80 text-xs uppercase tracking-wider flex items-center gap-2 mb-3">
                <User className="w-4 h-4" />Your Name
              </Label>
              <Input
                id="name" type="text" placeholder="Enter your name"
                value={formData.guest_name}
                onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                className="input-premium" data-testid="guest-name-input" required
              />
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message" className="text-wedding-ivory/80 text-xs uppercase tracking-wider flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4" />Your Message (Optional)
              </Label>
              <Textarea
                id="message" placeholder="Write your wishes..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="bg-transparent border border-white/20 focus:border-wedding-gold outline-none px-4 py-3 transition-colors duration-300 placeholder:text-white/20 text-wedding-ivory min-h-[120px] rounded-sm"
                data-testid="message-input"
              />
            </div>

            {/* Video Upload */}
            <div>
              <Label className="text-wedding-ivory/80 text-xs uppercase tracking-wider flex items-center gap-2 mb-3">
                <Video className="w-4 h-4" />Video Greeting (Optional)
              </Label>
              {videoPreview ? (
                <div className="relative">
                  <video src={videoPreview} className="w-full aspect-video object-cover rounded-sm bg-wedding-charcoal" controls data-testid="video-preview" />
                  <Button
                    type="button"
                    onClick={() => { setVideoFile(null); setVideoPreview(null); }}
                    className="absolute top-2 right-2 bg-wedding-black/80 text-wedding-ivory hover:bg-wedding-black"
                    size="sm" data-testid="remove-video-btn"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  className="w-full aspect-video bg-wedding-charcoal border border-dashed border-white/20 hover:border-wedding-gold/50 transition-colors flex flex-col items-center justify-center gap-4 rounded-sm"
                  data-testid="upload-video-btn"
                >
                  <Upload className="w-8 h-8 text-wedding-ivory/40" />
                  <div className="text-center">
                    <p className="text-wedding-ivory/60">Click to upload a video</p>
                    <p className="text-wedding-ivory/30 text-sm mt-1">MP4, MOV, or WebM (max 50MB)</p>
                  </div>
                </button>
              )}
            </div>

            {/* Submit */}
            <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="submit-btn">
              {submitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-wedding-black border-t-transparent rounded-full animate-spin" />
                  {uploadStep || "Submitting..."}
                </div>
              ) : (
                "Submit Message"
              )}
            </Button>
          </motion.form>
        </div>
      </main>
    </div>
  );
}
